import React, { useEffect, useMemo, useState } from 'react';
import styles from './styles/TopBar.module.css';
import TopBarMenu from './TopBarMenu';
import { useCanvas } from '../../../context/CanvasContext';
import { useEditorUI } from '../../../context/EditorUIContext';
import { FaEdit } from 'react-icons/fa';
import { displayNameFor, hashStringToColor, initialsFor, readableTextColor } from '../../../utils/collabColors';

/** How long "Edited by …" stays up after a collaborator's batch lands. */
const ATTRIBUTION_MS = 4000;
/** Chips shown before collapsing the rest into a "+N". */
const MAX_CHIPS = 3;

/**
 * Reports what the server knows, not what this tab hopes.
 *
 * `saveState` is the op queue's own state, and "unpublished changes" is a pure
 * derivation from `version > publishedVersion` — both numbers come back from the
 * server on every write, and `publishedVersion` is also pushed to us when somebody
 * else publishes. Nothing here maintains a dirty flag that could drift from the
 * truth.
 */
const SaveStatus = () => {
    const { saveState, saveError, hasUnpublishedChanges, publishedVersion } = useCanvas();

    if (saveState === 'error') {
        return (
            <span className={styles.statusError} title={saveError || undefined}>
                Not saved
            </span>
        );
    }
    if (saveState === 'saving' || saveState === 'pending') {
        return <span className={styles.statusSaving}>Saving…</span>;
    }
    if (hasUnpublishedChanges) {
        return (
            <span className={styles.statusDirty}>
                {publishedVersion === 0 ? 'Not published' : 'Unpublished changes'}
            </span>
        );
    }
    return <span className={styles.statusSaved}>All changes published</span>;
};

/**
 * One collaborator, as the colour they are drawn in on the canvas.
 *
 * The colour is the whole point of the chip: it is the same
 * `hashStringToColor(userId)` that outlines the node they have selected, so
 * "who is the person on my situation" is answerable without reading a name.
 */
const PresenceChip = ({ collaborator }) => {
    const color = hashStringToColor(collaborator.userId || collaborator.userEmail || '');

    return (
        <span
            className={styles.presenceChip}
            style={{ backgroundColor: color, color: readableTextColor(color) }}
            data-tooltip-id="tooltip"
            data-tooltip-content={displayNameFor(collaborator)}
            aria-label={`${displayNameFor(collaborator)} is editing`}
        >
            {initialsFor(collaborator)}
        </span>
    );
};

/**
 * Who else is in the editor, and who last changed something.
 *
 * The roster is presence from Redis, so it is honest about people who closed the
 * tab as well as people who left properly (server/collabStore.js prunes on read
 * and the heartbeat announces the sweep). The attribution line reads
 * `lastRemoteEdit`, which is `canvasOpsApplied`'s `userId` — sent since the draft
 * model shipped and, until now, thrown away by every client that received it.
 */
const Presence = () => {
    const { lastRemoteEdit } = useCanvas();
    const { otherCollaborators } = useEditorUI();

    const [attribution, setAttribution] = useState(null);

    useEffect(() => {
        if (!lastRemoteEdit?.userId) return undefined;

        setAttribution(lastRemoteEdit.userId);
        const timer = setTimeout(() => setAttribution(null), ATTRIBUTION_MS);
        // A second edit before the first faded restarts the clock rather than
        // letting the original timer cut the new one short.
        return () => clearTimeout(timer);
    }, [lastRemoteEdit]);

    const editorName = useMemo(() => {
        if (!attribution) return null;
        const match = otherCollaborators.find((entry) => entry?.userId === attribution);
        // Somebody can edit and leave before the badge fades, so there is not
        // always a roster entry to name them from.
        return match ? displayNameFor(match) : null;
    }, [attribution, otherCollaborators]);

    if (otherCollaborators.length === 0 && !attribution) return null;

    const shown = otherCollaborators.slice(0, MAX_CHIPS);
    const overflow = otherCollaborators.length - shown.length;

    return (
        <div className={styles.presenceRow}>
            {shown.map((collaborator) => (
                <PresenceChip
                    key={collaborator.userId || collaborator.userEmail}
                    collaborator={collaborator}
                />
            ))}

            {overflow > 0 && (
                <span
                    className={`${styles.presenceChip} ${styles.presenceOverflow}`}
                    data-tooltip-id="tooltip"
                    data-tooltip-content={otherCollaborators
                        .slice(MAX_CHIPS)
                        .map(displayNameFor)
                        .join(', ')}
                >
                    {`+${overflow}`}
                </span>
            )}

            {attribution && (
                <span className={styles.attribution}>
                    {editorName ? `Edited by ${editorName}` : 'Edited by a collaborator'}
                </span>
            )}
        </div>
    );
};

const TopBar = ({ renderEditSidebar, setRenderEditSidebar, isMobile }) => {
    const { scenario } = useCanvas();
    const { clearSelection } = useEditorUI();

    const toggleEditSidebar = () => {
        if (isMobile && renderEditSidebar) {
            setRenderEditSidebar(false);
            clearSelection();
        } else {
            setRenderEditSidebar(!renderEditSidebar);
        }
    };

    return (
        <div className={styles.container}>
            {/* Left Section */}
            <div className={styles.leftSection}>
                <TopBarMenu />
            </div>

            {/* Middle Section */}
            <div className={styles.middleSection}>
                <div className={styles.titleContainer}>
                    <h1>{scenario?.title || 'Untitled Scenario'}</h1>
                </div>
                <SaveStatus />
                {/*
                  * `otherCollaborators`, not the whole roster: everyone in the
                  * editor is in the collaboration room, so the roster includes you,
                  * and listing yourself as a collaborator on your own scenario
                  * reads as a bug.
                  */}
                <Presence />
            </div>

            {/* Right Section */}
            <div className={styles.rightSection}>
                <div
                    className={`${styles.item} ${renderEditSidebar ? styles.itemActive : ''}`}
                    onClick={toggleEditSidebar}
                    data-tooltip-id="tooltip"
                    data-tooltip-content="Edit Tools"
                >
                    <FaEdit />
                </div>
            </div>
        </div>
    );
};

export default TopBar;
